import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import type { AdminInvestorListItem } from '@workspace/api-backend'
import type { TableColumn } from '@workspace/ui/components/data-table'
import { CellPrimary, CellText, DataTable } from '@workspace/ui/components/data-table'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { Pagination } from '@workspace/ui/components/pagination'
import { SearchInput } from '@workspace/ui/components/search-input'
import { useState } from 'react'
import { useAdminInvestorsQuery } from '../../../features/admin/hooks/use-admin-api'

type SearchParams = {
  page?: number
  search?: string
}

export const Route = createFileRoute('/admin/investors/')({
  component: AdminInvestorsPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    page: Number(search.page) || 1,
    search: (search.search as string) || undefined,
  }),
})

const LIMIT = 20

function formatDate(date: string): string {
  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const columns: TableColumn<AdminInvestorListItem>[] = [
  {
    header: 'ID',
    width: '80px',
    render: (investor) => (
      <Link
        to="/admin/investors/$id"
        params={{ id: investor.id }}
        className="font-mono text-xs text-primary hover:underline"
      >
        {investor.id.slice(0, 8)}
      </Link>
    ),
  },
  {
    header: 'Name',
    render: (investor) => <CellPrimary>{investor.name}</CellPrimary>,
  },
  {
    header: 'Slug',
    width: '200px',
    render: (investor) => <CellText>{investor.slug ?? '\u2014'}</CellText>,
  },
  {
    header: 'Owner',
    render: (investor) =>
      investor.owner ? (
        <Link
          to="/admin/users/$id"
          params={{ id: investor.owner.id }}
          className="text-sm text-primary hover:underline"
        >
          {investor.owner.name || investor.owner.email}
        </Link>
      ) : (
        <CellText>{'\u2014'}</CellText>
      ),
  },
  {
    // Recency-weighted, shrunk acceptance rate normalised to the global rate.
    // 1.0 is neutral and is also what "no send history" looks like, so the two
    // are intentionally indistinguishable here — both mean "no evidence to act
    // on". Below 1.0 demotes the fund in retrieval and in send order.
    header: 'Engagement',
    width: '110px',
    render: (investor) => {
      const factor = investor.engagementFactor
      const tone =
        factor < 0.8 ? 'text-destructive' : factor > 1.0 ? 'text-primary' : 'text-muted-foreground'
      return (
        <span className={`font-medium tabular-nums ${tone}`} title="Engagement factor">
          {factor.toFixed(2)}
        </span>
      )
    },
  },
  {
    header: 'Credits',
    width: '90px',
    render: (investor) => (
      <span className="font-medium tabular-nums">{investor.creditBalance}</span>
    ),
  },
  {
    header: 'Created',
    width: '200px',
    render: (investor) => <CellText>{formatDate(investor.createdAt)}</CellText>,
  },
]

function AdminInvestorsPage() {
  const { page = 1, search } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const [searchInput, setSearchInput] = useState(search ?? '')

  const investorsQuery = useAdminInvestorsQuery({
    page,
    limit: LIMIT,
    search: search || undefined,
  })

  const investors = investorsQuery.data?.data?.items ?? []
  const total = investorsQuery.data?.data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / LIMIT))

  return (
    <div className="w-full space-y-4">
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <SearchInput
            placeholder="Search investors..."
            value={searchInput}
            onChange={setSearchInput}
            onSubmit={() => navigate({ search: { page: 1, search: searchInput || undefined } })}
          />
        </div>
        <Link to="/admin/investors/normalize-preferences">
          <SecondaryButton size="sm">Normalize preferences</SecondaryButton>
        </Link>
      </div>
      <DataTable
        columns={columns}
        data={investors}
        isLoading={investorsQuery.isLoading}
        emptyMessage="No investors found."
        keyExtractor={(i) => i.id}
      />
      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={(p) => navigate({ search: { page: p, search } })}
      />
    </div>
  )
}
